import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { formatErr, setAccessToken, setRefreshToken, clearAuthTokens } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = signed-in
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) setUser(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    // Global session-expired signal from the axios 401 interceptor.
    const onExpired = () => { clearAuthTokens(); setUser(false); };
    window.addEventListener("haccp-session-expired", onExpired);
    return () => {
      cancelled = true;
      window.removeEventListener("haccp-session-expired", onExpired);
    };
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Stash both tokens so subsequent requests work even when the edge
      // proxy strips credentialed-cookie CORS headers, AND so the silent
      // refresh interceptor can mint a new access token mid-shift.
      if (data?.access_token) setAccessToken(data.access_token);
      if (data?.refresh_token) setRefreshToken(data.refresh_token);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network failures during logout are non-fatal — we still clear local
      // state so the user is signed out client-side. Log for diagnostics.
      console.warn("Logout request failed (clearing local session anyway):", err?.message);
    }
    clearAuthTokens();
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, setUser }),
    [user, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
